// src/pages/Product.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Badge from "../components/ui/Badge";
import GoogleReviewsSection from "../sections/GoogleReviews";
import FAQ from "../components/FAQ";
import { FAQ_BETONROHRFALLE, FAQ_ABFANGKASTEN } from "../data/faq";
import Breadcrumbs from "../components/Breadcrumbs";
import TrustBadges from "../components/TrustBadges";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

/* --- kleines Toast/Popup ohne Libs --- */
function Toast({ show, title = "Hinzugefügt", text, onClose }) {
    if (!show) return null;
    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed bottom-6 right-6 z-[100] animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl shadow-2xl shadow-slate-200/50 dark:shadow-none p-5 min-w-[320px]">
                <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-600 shadow-lg shadow-brand-500/30">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <div className="flex-1 pt-0.5">
                        <div className="font-heading font-black text-slate-900 dark:text-white uppercase tracking-tight text-sm">{title}</div>
                        {text && <div className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed">{text}</div>}
                        
                        <div className="mt-3 flex items-center gap-3">
                            <Link to="/warenkorb" className="text-[10px] font-bold text-brand-600 hover:text-brand-700 uppercase tracking-widest flex items-center gap-1 group">
                                Zum Warenkorb
                                <span className="transition-transform group-hover:translate-x-1">→</span>
                            </Link>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-2 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        aria-label="Schließen"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                {/* Fortschrittsbalken unten */}
                <div className="absolute bottom-0 left-0 h-1 bg-brand-600 animate-progress-fast" style={{ width: '100%' }} />
            </div>
        </div>
    );
}

export default function Product({ addToCart }) {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);

    // Galerie
    const [idx, setIdx] = useState(0);
    const [fullIdx, setFullIdx] = useState(null); // Lightbox-Modus (index oder null)

    // Tabs
    const [tab, setTab] = useState("details"); // details | shipping | specs

    // Menge
    const [qty, setQty] = useState(1);

    // Varianten (z.B. 25cm / 30cm)
    const [variantId, setVariantId] = useState(null);

    // Ähnliche
    const [all, setAll] = useState([]);
    const [loadingAll, setLoadingAll] = useState(true);

    // Toast
    const [toast, setToast] = useState(false);

    useEffect(() => {
        setLoading(true);
        setIdx(0);
        fetch(`${API_BASE}/api/products.php?slug=${encodeURIComponent(slug)}`, { credentials: "omit" })
            .then((r) => r.json())
            .then((data) => {
                setP(data || null);
            })
            .catch(() => {
                setP(null);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        setLoadingAll(true);
        fetch(`${API_BASE}/api/products.php`, { credentials: "omit" })
            .then((r) => r.json())
            .then((arr) => setAll(Array.isArray(arr) ? arr : []))
            .catch(() => setAll([]))
            .finally(() => setLoadingAll(false));
    }, []);


    useEffect(() => {
        // Fallback, falls Produkt noch lädt / nicht gefunden
        const base = "Tuscher Helmut";
        if (p?.title) {
            document.title = `${p.title} - Jetzt kaufen - ${base}`;
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
                metaDesc.setAttribute("content", `${p.title}: ${p.description?.slice(0, 150)}... Profi-Fangsysteme direkt vom Jäger. Kostenlose Beratung & schneller Versand.`);
            }
        } else {
            document.title = `Produkt – ${base}`;
        }
    }, [p]);

    // Bilder sammeln: images_json (JSON-Array) > images (kommagetrennt) > image
    const images = useMemo(() => {
        if (!p) return [];
        const safe = [];
        if (p.images_json) {
            try {
                const parsed = JSON.parse(p.images_json);
                if (Array.isArray(parsed)) safe.push(...parsed);
            } catch { }
        }
        if (!safe.length && p.images) {
            const list = String(p.images)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            safe.push(...list);
        }
        if (!safe.length && p.image) {
            safe.push(String(p.image));
        }
        return safe.map((im) => `/${String(im).replace(/^\/+/, "")}`);
    }, [p]);

    // Ausgewählte Variante
    const selectedVariant = useMemo(() => {
        if (!p?.variants?.length || variantId == null) return null;
        return p.variants.find(v => Number(v.id) === Number(variantId)) || null;
    }, [p, variantId]);

    // Standard: erste Variante vorauswählen
    useEffect(() => {
        if (p?.variants?.length) {
            setVariantId(p.variants[0].id);
        } else {
            setVariantId(null);
        }
    }, [p]);

    // Effektiver Preis (Variante hat Vorrang)
    const displayPriceCents = useMemo(() => {
        if (!p) return 0;
        const vPrice = selectedVariant?.price_cents;
        const cents = vPrice != null && vPrice !== "" ? Number(vPrice) : Number(p.price_cents || 0);
        return Number.isFinite(cents) ? cents : 0;
    }, [p, selectedVariant]);

    // Streichpreis (Old Price)
    const oldPriceCents = useMemo(() => {
        if (!p) return null;
        const vOld = selectedVariant?.old_price_cents;
        const old = vOld != null && vOld !== "" ? Number(vOld) : (p.old_price_cents ? Number(p.old_price_cents) : null);
        return (old && old > displayPriceCents) ? old : null;
    }, [p, selectedVariant, displayPriceCents]);

    const priceEUR = useMemo(() => (displayPriceCents / 100).toFixed(2), [displayPriceCents]);
    const oldPriceEUR = useMemo(() => oldPriceCents ? (oldPriceCents / 100).toFixed(2) : null, [oldPriceCents]);

    const related = useMemo(() => {
        if (!p || !all.length) return [];
        const arr = all.filter((x) => x.slug !== p.slug);
        return arr.slice(0, 3);
    }, [p, all]);

    useEffect(() => {
        setTab("details");
        setQty(1);
    }, [slug]);

    // SEO: JSON-LD (Strukturierte Daten für Sterne & Preise bei Google)
    useEffect(() => {
        if (!p) return;
        
        // Preis formatieren (z.B. 249.00)
        const formattedPrice = (displayPriceCents / 100).toFixed(2);
        
        // JSON-LD Objekt bauen
        const ld = {
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": p.title,
            "image": images.map(img => typeof window !== "undefined" ? window.location.origin + img : img),
            "description": p.description || "",
            "sku": p.id,
            "brand": {
                "@type": "Brand",
                "name": "Tuscher Helmut"
            },
            "offers": {
                "@type": "Offer",
                "url": typeof window !== "undefined" ? window.location.href : "",
                "priceCurrency": "EUR",
                "price": formattedPrice,
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock",
                "itemCondition": "https://schema.org/NewCondition"
            },
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "5.0",
                "reviewCount": "12" // Wir können das später dynamisch machen, 12 ist ein guter Startwert
            }
        };

        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "product-jsonld";
        script.text = JSON.stringify(ld);
        
        const oldScript = document.getElementById("product-jsonld");
        if (oldScript) oldScript.remove();
        
        document.head.appendChild(script);
        
        return () => {
            const x = document.getElementById("product-jsonld");
            if (x) x.remove();
        };
    }, [p, images, displayPriceCents]);

        // Lieferumfang aufbereiten:
    // - included_json (JSON-Array-String) oder included (JSON-Array-String) -> Liste
    // - included_html (HTML) oder included (HTML) -> HTML
    // - included (Plaintext) -> nach Zeilenumbrüchen/; splitten -> Liste
    const includedParsed = useMemo(() => {
        if (!p) return { type: "fallback", list: null, html: null };

        const html = p.included_html ?? null;
        if (html && String(html).trim()) return { type: "html", list: null, html: String(html) };

        const raw = p.included ?? p.included_json ?? null;
        if (raw == null) return { type: "fallback", list: null, html: null };

        const s = String(raw).trim();
        if (!s) return { type: "fallback", list: null, html: null };

        if (/<\s*[a-z][\s\S]*>/i.test(s)) return { type: "html", list: null, html: s };

        try {
            const arr = JSON.parse(s);
            if (Array.isArray(arr) && arr.length) return { type: "list", list: arr.map(x => String(x)), html: null };
        } catch {}

        const parts = s.split(/\r?\n|;/g).map(x => x.trim()).filter(Boolean);
        if (parts.length) return { type: "list", list: parts, html: null };

        return { type: "fallback", list: null, html: null };
    }, [p]);


    if (loading) {
        return (
            <section className="mx-auto max-w-6xl px-4 py-20">
                <div className="grid md:grid-cols-2 gap-10">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 h-96 animate-pulse" />
                    <div className="space-y-3">
                        <div className="h-8 w-1/2 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                        <div className="h-6 w-1/4 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                        <div className="h-20 w-full bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                    </div>
                </div>
            </section>
        );
    }
    if (!p) {
        return (
            <section className="mx-auto max-w-6xl px-4 py-20">
                <p>Produkt nicht gefunden.</p>
                <Link to="/shop" className="mt-4 inline-block text-brand">Zurück zum Shop</Link>
            </section>
        );
    }

    const handleAdd = () => {
        addToCart?.(p.slug, qty, {
            title: p.title,
            variant_id: selectedVariant?.id ?? null,
            variant_label: selectedVariant?.label ?? null,
            price_cents: displayPriceCents,
            weight_grams: selectedVariant?.weight_grams ?? p?.weight_grams ?? null,
        });
        setToast(true);
        window.clearTimeout((handleAdd._t ||= 0));
        handleAdd._t = window.setTimeout(() => setToast(false), 2500);
    };

    
    const n = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
    const cm = (v) => (n(v) === null ? "–" : `${n(v).toFixed(1)} cm`);
    const kgFromGrams = (g) => (n(g) === null ? "–" : `${(n(g) / 1000).toFixed(1)} kg`);
    const effectiveSpecs = {
        weight_grams: selectedVariant?.weight_grams ?? p?.weight_grams ?? null,
        length: selectedVariant?.length ?? p?.length ?? null,
        width: selectedVariant?.width ?? p?.width ?? null,
        height: selectedVariant?.height ?? p?.height ?? null,
    };
return (
        <section className="mx-auto max-w-6xl px-4 py-14">
            {/* Breadcrumbs mit Schema.org */}
            <Breadcrumbs 
                items={[
                    { label: "Shop", href: "/shop" },
                    { label: p.title }
                ]}
                className="mb-6"
            />


            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Galerie */}
                <div>
                    <div 
                        className="group relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 h-full flex items-center justify-center cursor-zoom-in overflow-hidden"
                        onClick={() => setFullIdx(idx)}
                    >
                        {images.length ? (
                            <img
                                src={images[idx]}
                                alt={p.title}
                                className="max-h-[420px] w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                loading="eager"
                            />
                        ) : (
                            <div className="h-80 w-full bg-slate-100 dark:bg-slate-800 rounded-lg" />
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                            <svg className="h-10 w-10 text-white/0 group-hover:text-white/80 transition-all drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                        </div>
                    </div>

                    {images.length > 1 && (
                        <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2">
                            {images.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIdx(i)}
                                    className={`h-16 rounded-lg border p-1 flex items-center justify-center overflow-hidden
                    ${i === idx
                                            ? "border-brand ring-1 ring-brand"
                                            : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"}`}
                                    aria-label={`Bild ${i + 1}`}
                                >
                                    <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info/Buy */}
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        {oldPriceEUR && (
                            <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200">
                                Angebot
                            </Badge>
                        )}
                        <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            Auf Lager
                        </Badge>
                    </div>

                    <h1 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight">{p.title}</h1>

                    <div className="mt-4 flex flex-col gap-1">
                        <div className="flex items-baseline gap-3">
                            <div className="text-3xl font-black text-brand-600 tabular-nums">{priceEUR} €</div>
                            {oldPriceEUR && (
                                <>
                                    <div className="text-xl text-slate-400 line-through decoration-red-500/50 tabular-nums">
                                        {oldPriceEUR} €
                                    </div>
                                    <div className="px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-bold uppercase tracking-wider animate-bounce">
                                        -{Math.round((1 - (displayPriceCents / oldPriceCents)) * 100)}% gespart
                                    </div>
                                </>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-500 font-medium italic">
                            Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.
                        </p>
                    </div>

                    {p.description && (
                        <p className="mt-3 text-slate-700 dark:text-slate-300">{p.description}</p>
                    )}

{p?.variants?.length ? (
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                                Ausführung wählen
                            </label>
                            <select
                                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                value={variantId ?? ""}
                                onChange={(e) => setVariantId(e.target.value)}
                            >
                                {p.variants.map((v) => {
                                    const vCents = v.price_cents != null && v.price_cents !== "" ? Number(v.price_cents) : null;
                                    const vPrice = vCents != null ? (vCents / 100).toFixed(2) : null;
                                    return (
                                        <option key={v.id} value={v.id}>
                                            {v.label}{vPrice ? ` – ${vPrice} €` : ""}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    ) : null}


                    {/* Features (optional) */}
                    {p.features_json && (() => {
                        try {
                            const features = JSON.parse(p.features_json);
                            if (Array.isArray(features) && features.length) {
                                return (
                                    <ul className="mt-4 grid gap-2 list-disc pl-5 text-slate-700 dark:text-slate-300">
                                        {features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                );
                            }
                        } catch { }
                        return null;
                    })()}

                    {/* Menge + Buttons */}
                    <div className="mt-6 flex items-center gap-3">
                        <div className="flex items-center border border-slate-300 dark:border-slate-700 rounded-lg">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800"
                                aria-label="Menge verringern"
                            >
                                −
                            </button>
                            <input
                                type="number"
                                min="1"
                                value={qty}
                                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                                className="w-14 text-center bg-transparent"
                            />
                            <button
                                onClick={() => setQty((q) => q + 1)}
                                className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800"
                                aria-label="Menge erhöhen"
                            >
                                +
                            </button>
                        </div>

                        <button
                            onClick={handleAdd}
                            className="rounded-md bg-brand text-white px-5 py-3 font-medium hover:bg-brandDark"
                        >
                            In den Warenkorb
                        </button>

                        <Link
                            to="/shop"
                            className="rounded-md border border-brand text-brand px-5 py-3 font-medium hover:bg-brand/10"
                        >
                            Zurück zum Shop
                        </Link>
                    </div>

                    <p className="mt-4 text-xs text-slate-500">Art.-Nr.: {p.id}</p>

                    {/* Trust Badges */}
                    <TrustBadges layout="vertical" className="mt-6" />

                    {/* Tabs */}
                    <div className="mt-8">
                        <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
                            {[
                                { id: "details", label: "Details" },
                                { id: "shipping", label: "Versand" },
                                { id: "specs", label: "Spezifikation" },
                            ].map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setTab(t.id)}
                                    className={`px-2 pb-2 -mb-px border-b-2 text-sm ${tab === t.id
                                            ? "border-brand text-brand"
                                            : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                        }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                    <div className="mt-4 text-sm text-slate-700 dark:text-slate-300">
                        {tab === "details" && (() => {
                            // Unterstütze mehrere mögliche Keys (falls PHP anders heißt)
                            const raw =
                                p.details ??
                                p.details_html ??
                                p.long_description_html ??
                                p.long_description ??
                                "";

                            const text = String(raw ?? "").trim();

                            if (!text) {
                                return <p>Robuste Ausführung, praxisbewährt und wartungsarm. Weitere Details folgen.</p>;
                            }

                            // Wenn HTML drin ist => als HTML rendern
                            const looksLikeHtml = /<\s*[a-z][\s\S]*>/i.test(text);

                            return looksLikeHtml ? (
                                <div
                                    className="prose dark:prose-invert prose-slate max-w-none"
                                    dangerouslySetInnerHTML={{ __html: text }}
                                />
                            ) : (
                                <p className="whitespace-pre-line">{text}</p>
                            );
                        })()}

                        {tab === "shipping" && (
                            <ul className="list-disc pl-5 space-y-1">
                                <li>Eine Abholung ist nach vorheriger Vereinbarung jederzeit möglich.</li>
                                <li>Lieferung per DHL oder Spedition – abhängig von Gewicht/Abmessung.</li>
                                <li>Versandkosten werden im Checkout nach PLZ-Zone und Gewicht berechnet.</li>
                                {p.requires_freight ? <li><strong>Dieses Produkt wird per Spedition versendet.</strong></li> : null}
                            </ul>
                        )}

                            {tab === "specs" && (
                                p.specs_html ? (
                                    <div
                                        className="prose dark:prose-invert prose-slate max-w-none"
                                        dangerouslySetInnerHTML={{ __html: p.specs_html }}
                                    />
                                ) : (
                                    <table className="w-full text-sm">
                                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                            <tr>
                                                <td className="py-2 text-slate-500">Gewicht</td>
                                                <td className="py-2">{kgFromGrams(effectiveSpecs.weight_grams)}</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 text-slate-500">Länge</td>
                                                <td className="py-2">{cm(effectiveSpecs.length)}</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 text-slate-500">Breite</td>
                                                <td className="py-2">{cm(effectiveSpecs.width)}</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 text-slate-500">Höhe</td>
                                                <td className="py-2">{cm(effectiveSpecs.height)}</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 text-slate-500">Artikelnummer</td>
                                                <td className="py-2">{p.id}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Lieferumfang */}
            <div className="mt-12">
                <h2 className="text-xl font-semibold">Lieferumfang</h2>
                <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                    {includedParsed.type === "list" ? (
                        <ul className="list-disc pl-5 space-y-1">
                            {includedParsed.list.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                    ) : includedParsed.type === "html" ? (
                        <div
                            className="prose dark:prose-invert prose-slate max-w-none"
                            dangerouslySetInnerHTML={{ __html: includedParsed.html }}
                        />
                    ) : (
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Produkt gemäß Beschreibung</li>
                            <li>Kurzanleitung / Hinweise zur Inbetriebnahme</li>
                            <li>Rechnung mit ausgewiesener MwSt.</li>
                        </ul>
                    )}
                </div>
            </div>

            {/* Ähnliche Produkte */}
            <div className="mt-12">
                <h2 className="text-xl font-semibold">Andere haben sich auch angesehen</h2>
                {loadingAll ? (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 animate-pulse">
                                <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded" />
                                <div className="mt-3 h-5 w-2/3 bg-slate-200 dark:bg-slate-800 rounded" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/produkt/${r.slug}`}
                                className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-md transition"
                            >
                                <div className="h-40 bg-slate-100 dark:bg-slate-800 rounded-lg grid place-items-center overflow-hidden">
                                    {r.image ? (
                                        <img
                                            src={`/${String(r.image).replace(/^\/+/, "")}`}
                                            alt={r.title}
                                            className="max-h-36 object-contain group-hover:scale-[1.02] transition"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="text-slate-400">Kein Bild</div>
                                    )}
                                </div>
                                <h3 className="mt-3 font-semibold">{r.title}</h3>
                                <div className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                                    {r.description || "Zum Produkt"}
                                </div>
                                <div className="mt-3 flex items-center justify-between">
                                    <span className="font-bold">
                                        {(Number(r.price_cents || 0) / 100).toFixed(2)} €
                                    </span>
                                    <span className="text-brand text-sm">Details →</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* FAQ Section - mit Schema.org für Google Rich Snippets */}
            {slug?.includes("betonrohrfalle") && (
                <FAQ 
                    items={FAQ_BETONROHRFALLE}
                    title="Häufige Fragen zur Betonrohrfalle"
                    subtitle="Alles was Sie vor dem Kauf wissen sollten"
                    schemaId="faq-betonrohrfalle"
                />
            )}
            {slug?.includes("abfangkasten") && (
                <FAQ 
                    items={FAQ_ABFANGKASTEN}
                    title="Häufige Fragen zum Abfangkasten"
                    subtitle="Tipps zur Wildentnahme"
                    schemaId="faq-abfangkasten"
                />
            )}

            {/* Reviews */}
            <div className="mt-16">
                <GoogleReviewsSection
                    title="Was meine Kundinnen & Kunden sagen"
                    subtitle="Unverändert aus meinen Google-Bewertungen."
                    ctaTo="https://g.page/r/CcB_B2dngBtxEBM/review"
                />
            </div>

            {/* Toast */}
            <Toast
                show={toast}
                title="Zum Warenkorb hinzugefügt"
                text={`${p.title} × ${qty}`}
                onClose={() => setToast(false)}
            />

            {/* Lightbox Overlay */}
            {fullIdx !== null && (
                <div 
                    className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 sm:p-10 animate-in fade-in duration-300"
                    onClick={() => setFullIdx(null)}
                >
                    <button className="absolute top-6 right-6 p-2 rounded-full bg-slate-800 text-white hover:bg-slate-700 transition-colors">
                        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    
                    <div className="relative w-full max-w-5xl h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <img 
                            src={images[fullIdx]} 
                            alt={p.title} 
                            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
                        />
                        
                        {/* Navigation inside Lightbox */}
                        {images.length > 1 && (
                            <>
                                <button 
                                    onClick={() => setFullIdx((fullIdx - 1 + images.length) % images.length)}
                                    className="absolute left-0 sm:-left-16 p-3 rounded-full bg-white/5 hover:bg-white/10 text-white transition-all backdrop-blur-md"
                                >
                                    <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <button 
                                    onClick={() => setFullIdx((fullIdx + 1) % images.length)}
                                    className="absolute right-0 sm:-right-16 p-3 rounded-full bg-white/5 hover:bg-white/10 text-white transition-all backdrop-blur-md"
                                >
                                    <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
